import { useEffect, useRef } from 'react';

import { cvRepository } from '@/services/db/repositories/cvRepository';
import { pullAllFromCloud, uploadCv, watchRemoteCvs } from '@/services/sync/syncService';
import { useAuthStore } from '@/stores/authStore';
import { useCvStore } from '@/stores/cvStore';
import { useSettingsStore } from '@/stores/settingsStore';

const DEBOUNCE_MS = 3000;

export function useCloudSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const encryptionKey = useAuthStore((s) => s.encryptionKey);
  const syncEnabled = useSettingsStore((s) => s.syncEnabled);
  const isDirty = useCvStore((s) => s.isDirty);
  const lastSavedAt = useCvStore((s) => s.lastSavedAt);
  const currentCvId = useCvStore((s) => s.currentCvId);
  const setSyncStatus = useAuthStore((s) => s.setSyncStatus);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUploadedAt = useRef<string | null>(null);
  const initialPullDone = useRef(false);

  useEffect(() => {
    if (!syncEnabled || !isAuthenticated || !encryptionKey || initialPullDone.current) return;
    initialPullDone.current = true;
    setSyncStatus('syncing');
    void pullAllFromCloud()
      .then(() => {
        setSyncStatus('synced');
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        setSyncStatus('error', message);
      });
  }, [syncEnabled, isAuthenticated, encryptionKey, setSyncStatus]);

  useEffect(() => {
    if (!syncEnabled || !isAuthenticated || !encryptionKey || !currentCvId) return;
    if (isDirty) return;
    if (!lastSavedAt) return;
    if (lastUploadedAt.current === lastSavedAt) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        const cv = await cvRepository.getById(currentCvId);
        if (!cv) return;
        try {
          await uploadCv(cv);
          lastUploadedAt.current = lastSavedAt;
        } catch (err) {
          console.error('Sync upload failed', err);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [syncEnabled, isAuthenticated, encryptionKey, currentCvId, isDirty, lastSavedAt]);

  useEffect(() => {
    if (!syncEnabled || !isAuthenticated) return;
    const handleOnline = () => setSyncStatus('synced');
    const handleOffline = () => setSyncStatus('offline');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (!navigator.onLine) setSyncStatus('offline');
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncEnabled, isAuthenticated, setSyncStatus]);

  useEffect(() => {
    if (!syncEnabled || !isAuthenticated || !encryptionKey) return;
    let unsub: (() => void) | null = null;
    let cancelled = false;
    void (async () => {
      try {
        unsub = await watchRemoteCvs(async (cv) => {
          const local = await cvRepository.getById(cv.id);
          if (!local || cv.metadata.updatedAt > local.metadata.updatedAt) {
            await cvRepository.save(cv);
          }
        });
      } catch (err) {
        if (!cancelled) console.error('Listener error', err);
      }
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [syncEnabled, isAuthenticated, encryptionKey]);
}
